import React, { useEffect, useRef, useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { LocalizedStrings } from '@src/utils/localizedStrings';

import { StringHelper } from '@src/services/helpers/string';
import { CollectionView } from 'common';
import dayjs from 'dayjs';
import { useQueryCollectionContentNumber } from '@src/queries/collections/useQueryCollectionContentNumber';
import { useMutationCollectionDelete } from '@src/queries/collections/useMutationCollectionDelete';
import { style, classes } from 'typestyle';
import { ADALONG_COLORS, ANALYZE_COLORS } from '@src/styles';
import { useAddAlert } from '@src/fragments/Alerting/alertAtom';
import { getLangError } from '../../../../fragments/Alerting/AlertSnackbar';
import { Loader } from '../../../../components/Loader';
import { setPopup } from '@src/state/popupAtoms';
import { EPopup } from '@src/types/common';
import { CollectionTypeBadge } from './CollectionTypeBadge';
import { useMutationCollectionSetAsFavorite } from '@src/queries/collections/useMutationCollection';
import * as appHelpers from '@src/services/helpers/appHelpers';
import { Favorite, FavoriteBorder } from '@mui/icons-material';

interface Props {
  collection: CollectionView;
  onClick: () => void;
  full?: boolean;
  backgroudImageUrl?: string;
  className?: string;
  onDeleted?: (collectionId: string) => void;
  inCollectionMark?: boolean;
}

export function CollectionThumb(props: Props) {
  const [visible, setVisible] = useState(false);
  const [isFavorite, setIsFavorite] = useState(props.collection.isFavorite ?? false);
  const addAlert = useAddAlert();
  const infoRef = useRef<HTMLDivElement | null>(null);

  // Sync from parent data after refetch
  useEffect(() => {
    setIsFavorite(props.collection.isFavorite ?? false);
  }, [props.collection.isFavorite]);

  const { data: contentNumber } = useQueryCollectionContentNumber(props.collection._id, {
    enabled: visible,
  });
  const { mutateAsync: deleteCollection, reset: resetDelete } = useMutationCollectionDelete();
  const { mutateAsync: setAsFavorite, reset: resetSetAsFavorite, isPending: isFavoriteLoading } = useMutationCollectionSetAsFavorite();

  const onToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFavoriteLoading) return;
    const nextIsFavorite = !isFavorite;
    setIsFavorite(nextIsFavorite); // optimistic update
    setAsFavorite(
      {
        collectionId: props.collection._id,
        isFavorite: nextIsFavorite,
      },
      {
        onSuccess: () => {
          appHelpers.addAlert({
            text: nextIsFavorite ? lang.favorite.set : lang.favorite.unset,
            type: 'success',
          });
        },
        onError: (error) => {
          setIsFavorite(!nextIsFavorite); // revert
          addAlert({
            text: getLangError(error.type, lang.errors),
            close: resetSetAsFavorite,
          });
        },
      }
    ).catch(console.error);
  };

  const onDelete = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setPopup(EPopup.promptconfirm, {
        question: window.T('create.collections.confirmremove'),
        text: { yes: 'DELETE', no: 'CANCEL' },
        yesColor: 'red',
        answer: (yes) => {
          if (!yes) {
            return;
          }
          deleteCollection(
            { collectionId: props.collection._id },
            {
              onSuccess: () => props.onDeleted?.(props.collection._id),
              onError: (error) =>
                addAlert({
                  text: getLangError(error.type, lang.errors),
                  close: resetDelete,
                }),
            }
          ).catch(console.error);
        },
    });
  };

  /* Arrow function for binding */
  useEffect(() => {
    if (visible) {
      return;
    }
    const node = infoRef.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || !(window as any).IntersectionObserver) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  if (!props.collection) {
    return null;
  }

  const { name } = props.collection;
  const formattedDate = dayjs(props.collection.created_at).format('DD.MM.YY');

  return (
    <div className={classes(collectionThumbClass, props.className)} onClick={() => props.onClick()}>
      <div
        className={coverClass}
        aria-hidden='true'
        style={props.backgroudImageUrl ? { backgroundImage: `url(${props.backgroudImageUrl})` } : undefined}
      >
        <CollectionTypeBadge
          type={props.collection.type}
          manualLabel={lang.collectionType.manual}
          autoLabel={lang.collectionType.auto}
        />
        {props.inCollectionMark ? (
          <div className={inCollectionMarkClass}>
            <span>{lang.inCollectionMark}</span>
          </div>
        ) : null}
        <div className={iconsGroupClass}>
          <div
            className={classes(iconBtnClass, isFavorite ? undefined : favIconHiddenClass)}
            title={isFavorite ? lang.favorite.unset : lang.favorite.set}
            onClick={onToggleFavorite}
            style={isFavoriteLoading ? { opacity: 0.3, pointerEvents: 'none' } : undefined}
          >
            {isFavorite
              ? <Favorite style={{ fontSize: 16, color: ANALYZE_COLORS.ROSE }} />
              : <FavoriteBorder style={{ fontSize: 16 }} />
            }
          </div>
          {!!props.full && props.collection.mine ? (
            <div
              className={classes(iconBtnClass, deleteIconClass)}
              title={StringHelper.upperFirst(window.T('create.collections.remove'))}
              onClick={onDelete}
            >
              <i className='material-icons notranslate' style={{ fontSize: 16, lineHeight: 1 }}>delete</i>
            </div>
          ) : null}
        </div>
      </div>
      <div ref={infoRef} className={infoClass}>
        <div className={titleClass}>{StringHelper.upperFirst(name)}</div>
        <div className={metaClass}>
          <span className={metaValueClass}>
            {typeof contentNumber !== 'undefined' ? (
              <NumberFormat value={contentNumber} displayType='text' thousandSeparator />
            ) : (
              <Loader size={8} />
            )}
          </span>
          <span className={metaIconWrapper}>
            <i className={classes(metaIconClass, 'material-icons', 'notranslate')}>
              image
            </i>
          </span>
          <span className={metaSeparatorClass}>&bull;</span>
          <span className={dateClass}>{formattedDate}</span>
        </div>
      </div>
    </div>
  );
}

const lang = LocalizedStrings({
  en: {
    errors: {
      usedAsWidgetSource:
        'This collection is used as Widget source. Please remove it from all widgets before deleting this collection',
      unknown: 'An error ocurred',
    },
    collectionType: {
      manual: 'Manual',
      auto: 'Auto',
    },
    favorite: {
      set: 'Set as favorite',
      unset: 'Removed from favorites',
    },
    inCollectionMark: 'In collection',
  },
});

const inCollectionMarkClass = style({
  position: 'absolute',
  right: 0,
  top: 20,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 4px',
  borderBottomLeftRadius: 100,
  borderTopLeftRadius: 100,
  background: ADALONG_COLORS.FLASH_GREEN,
  color: ADALONG_COLORS.DARK_BLACK,
  fontSize: 8,
  fontWeight: 600,
  letterSpacing: 0.2,
  textTransform: 'uppercase',
  boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
  pointerEvents: 'none',
  zIndex: 2,
});

const iconsGroupClass = style({
  position: 'absolute',
  top: 8,
  left: 8,
  display: 'flex',
  flexDirection: 'row',
  gap: 6,
  zIndex: 2,
});

const iconBtnClass = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.4)',
  color: '#fff',
  cursor: 'pointer',
  transition: 'background 0.2s ease, transform 0.15s ease, opacity 0.2s ease',
  opacity: 1,
  $nest: {
    '&:hover': {
      background: 'rgba(0,0,0,0.65)',
      transform: 'scale(1.1)',
    },
  },
});

const favIconHiddenClass = style({
  opacity: 0,
});

const deleteIconClass = style({
  opacity: 0,
});

const collectionThumbClass = style({
  position: 'relative',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: ADALONG_COLORS.SMOOTHER_BLACK,
  borderRadius: 12,
  cursor: 'pointer',
  color: '#f5f5f5',
  overflow: 'hidden',
  transition: 'transform 0.2s ease',
  $nest: {
    '&:hover': {
      transform: 'translateY(-2px)',
    },
    [`&:hover .${favIconHiddenClass}`]: {
      opacity: 1,
    },
    [`&:hover .${deleteIconClass}`]: {
      opacity: 1,
    },
  },
});

const coverClass = style({
  flex: '3 1 0',
  backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  position: 'relative',
  overflow: 'hidden',
});

const infoClass = style({
  flex: '1 1 0',
  padding: '10px 14px 12px',
  backgroundColor: ADALONG_COLORS.SMOOTHER_BLACK,
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 6,
});

const titleClass = style({
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.2,
  maxHeight: '2.4em',
  overflow: 'hidden',
  display: '-webkit-box',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  "-webkit-line-clamp": 2,
  WebkitBoxOrient: 'vertical',
});

const metaClass = style({
  fontSize: 11,
  color: '#c3c3c3',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 14,
});

const metaValueClass = style({
  fontWeight: 600,
});

const metaIconWrapper = style({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  color: '#bdbdbd',
});

const metaIconClass = style({
  fontSize: 12,
  lineHeight: 1,
});

const metaSeparatorClass = style({
  color: '#8d8d8d',
});

const dateClass = style({
  fontSize: 11,
  color: '#9c9c9c',
});
